import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { memo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Map from '../../components/Map/Map';
import config from '../../config';
import useIsInView from '../../util/hooks/useIsInView';
import { obfuscatedCoordinates } from '../../util/maps';
import ListingSection from './ListingSection';
import ListingSectionItem from './ListingSectionItem';

// former ListingPage.module.css .map
const mapSx = (theme) => ({
  // Dimensions: Map takes all available space from viewport (excludes action button and section title)
  height: '320px',
  width: '100%',
  // Static map: dimensions are 640px
  maxWidth: '100%',
  maxHeight: '640px',
  backgroundColor: 'white',
  padding: 0,
  border: 0,
  cursor: 'pointer',
  [theme.breakpoints.up('md')]: {
    maxWidth: '100%',
  },
  [theme.breakpoints.up('lg')]: {
    height: '640px',
  },
});

/**
 * A component for showing a map of the listing location.
 * If the map is not in the viewport, the map is shown as a static image.
 * @param {object} props
 * @param {object} props.geolocation - Geolocation object
 * @param {object} props.location - Location object
 * @param {object} props.listingId - Listing
 * @returns {React.JSX.Element|null}
 */

const SectionMapMaybeComponent = ({ geolocation = null, location = null, listingId = null }) => {
  const ref = useRef();
  const inViewport = useIsInView(ref, { threshold: 0.5 }, true);
  const [isStatic, setIsStatic] = useState(true);

  if (!geolocation) {
    return null;
  }

  const address = location ? location.address : '';
  const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

  const mapProps = config.maps.fuzzy.enabled
    ? { obfuscatedCenter: obfuscatedCoordinates(geolocation, cacheKey) }
    : { address, center: geolocation };
  const map = <Map {...mapProps} useStaticMap={isStatic} />;

  return (
    <ListingSection ref={ref}>
      <ListingSectionItem params={{ size: { lg: 12 } }}>
        <Typography
          component="h3"
          mb={2}
          sx={{ textAlign: { xs: 'center', md: 'left', width: '100%' } }}
          variant="h4"
        >
          <FormattedMessage id="ListingPage.locationTitle" />
        </Typography>
      </ListingSectionItem>
      <ListingSectionItem params={{ size: { lg: 12 } }}>
        {isStatic ? (
          <Box
            aria-label="Show detailed map" // Descriptive label for the button
            component="button"
            onClick={() => {
              setIsStatic(false);
            }}
            sx={mapSx}
          >
            {inViewport ? <>{map}</> : null}
          </Box>
        ) : (
          <Box sx={mapSx}>{map}</Box>
        )}
      </ListingSectionItem>
    </ListingSection>
  );
};

SectionMapMaybeComponent.displayName = 'SectionMapMaybe';

const SectionMapMaybe = memo(SectionMapMaybeComponent);

export default SectionMapMaybe;
